import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CN from "classnames";
import { forwardRef } from "react";
import { styled } from "@mui/material/styles";

/**
 * Primary component for displaying a large list, which can have icons, be
 * clickable, and show selected state.
 *
 * If the component passes `onClick` or `href`, it will be considered clickable.
 * Clickable variants look like buttons and don't align with non-clickable
 * variants.
 *
 * It is not necessary to wrap this component in a list parent or container.
 *
 * Example use cases:
 * - Showing a list of options for the user to select, where each option has
 *   some title and descriptions.
 * - Showing a series of information statements with icons. (If you don't need
 *   icons, a regular list of paragraphs is best.)
 */
export const DreamMuiLargeListItem = forwardRef<
  HTMLDivElement,
  Omit<React.ComponentProps<typeof Wrapper>, "title"> & {
    href?: string;
    /**
     * For lists of selectable options, consider icons that convey the option can
     * be selected, such as `faCheckCircle`. For a list of radio buttons, use
     * `faCheckCircle` in conjunction with hollow `faCircle`.
     */
    icon?: React.ReactNode;
    endIcon?: React.ReactNode;
    title?: React.ReactNode;
    children?: React.ReactNode;
    className?: string;

    /**
     * Applies padding, a dark background, and white text.
     * Consider using `faCheckCircle` icon as well.
     */
    isSelected?: boolean;
    disabled?: boolean;

    /**
     * Select a display variant (Note: to implicitly specify the `clickable`
     * variant, just pass the `onClick` prop. If the component should align with
     * other clickable items but not be clickable, use the `clickable` variant.
     *
     * Tangent variants:
     * - Normal
     * - Clickable: Visually identical to actually being clickable, except that
     *   hover effects are not enabled.
     */
    variant?: "normal" | "clickable";
    color?: "normal" | "success" | "error";
  }
>(
  (
    {
      icon,
      title,
      children,
      className,
      variant = "normal",
      color = "normal",
      isSelected = false,
      onClick,
      disabled,
      endIcon,
      ...propsToWrapper
    },
    ref
  ) => {
    const hasOnClick = onClick || propsToWrapper.href;

    return (
      <Wrapper
        {...(!!propsToWrapper?.href && { component: "a" })}
        ref={ref}
        className={CN(
          "wrapper",
          variant,
          color,
          {
            hasOnClick,
            selected: isSelected,
            disabled,
          },
          className
        )}
        onClick={disabled ? () => undefined : onClick}
        {...propsToWrapper}
      >
        {!!icon && <Icon className="start-icon">{icon}</Icon>}
        <Content sx={{ mt: 0.5 }}>
          {!!title && (
            <Title variant="new_h3" sx={{ mt: 0, mb: 0.5 }}>
              {title}
            </Title>
          )}
          {children}
        </Content>
        {!!endIcon && <Icon className="end-icon">{endIcon}</Icon>}
      </Wrapper>
    );
  }
);

const Wrapper = styled(Box)`
  min-width: 100%;
  padding: 1rem 0;
  border-top: 1px solid ${({ theme }) => theme.palette.dream.grayLight};
  border-bottom: 1px solid ${({ theme }) => theme.palette.dream.grayLight};

  text-decoration: none;

  // These lines remove styling from if this is rendered as an anchor tag
  display: flex;
  color: unset;

  &:not(.hasOnClick) + &:not(.hasOnClick) {
    border-top-width: 0;
  }

  &.clickable,
  &.hasOnClick,
  &.selected {
    border: 1px solid ${({ theme }) => theme.palette.dream.grayLight};
    border-bottom-width: 2px;
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  &.hasOnClick.disabled {
    user-select: none;

    &:hover {
      cursor: default;
      background: unset;
    }
  }

  &.hasOnClick {
    cursor: pointer;
    transition: 100ms linear all;

    &:hover {
      background: ${({ theme }) => theme.palette.dream.grayLighter};
    }

    &:focus,
    &:active {
      background: ${({ theme }) => theme.palette.dream.grayLight};
    }
  }

  &.selected {
    background: ${({ theme }) => theme.palette.dream.activeBackground};
    border-color: rgba(0, 0, 0, 0.2);

    &.hasOnClick.disabled:hover {
      background: ${({ theme }) => theme.palette.dream.activeBackground};
    }

    &.hasOnClick:hover {
      background: ${({ theme }) => theme.palette.dream.activeBackgroundHover};
    }
  }
`;

const Icon = styled(Box)`
  width: 2rem;
  font-size: 1.4rem;
  text-align: center;
  color: ${({ theme }) => theme.palette.primary.main};

  &.start-icon {
    margin-right: ${({ theme }) => theme.spacing(2)};
    .wrapper.success & {
      color: ${({ theme }) => theme.palette.success.main};
    }

    .wrapper.error & {
      color: ${({ theme }) => theme.palette.error.main};
    }
  }

  &.end-icon {
    margin-left: ${({ theme }) => theme.spacing(2)};
    margin-right: ${({ theme }) => theme.spacing(-1)};
  }
`;

const Title = styled(Typography)`
  flex: 0 0;
`;

const Content = styled(Box)`
  flex: 1 1;
  min-width: 0;

  & > *:last-child {
    margin-bottom: 0;
  }
`;
